import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";

export const Route = createFileRoute("/print/$orderId/receipt")({
  validateSearch: (raw: Record<string, unknown>) => ({
    tid: typeof raw.tid === "string" ? raw.tid : "",
  }),
  component: ReceiptPrintPage,
  head: () => ({
    meta: [
      { title: "Ticket — Cliente" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type Selection = { addonProductId: string; name: string; cantidad: number; precioUnitario: number };
interface PrintItem {
  id: string; productName: string; tipoVenta: string;
  cantidad: number; unidadMedida: string;
  precioUnitario: number | null; precioPorKg: number | null; precioTotal: number;
  variantName: string | null; selections: Selection[];
}
interface PrintData {
  order: {
    id: string; customerName: string; customerEmail: string; customerPhone: string | null;
    status: string; metodEntrega: string; direccionEntrega: string | null;
    tienePesables: boolean; pesoRealKg: number | null;
    precioEstimado: number; precioFinal: number | null;
    paymentStatus: string; paymentMethod: string | null;
    notes: string | null;
    bankTransferHolder?: string | null; bankTransferAlias?: string | null; bankTransferCbu?: string | null;
    fulfillmentMode?: string; scheduledFor?: string | null;
    receiptPrintedAt: string | null; receiptPrintCount: number;
    createdAt: string;
  };
  items: PrintItem[];
  tenant: { name: string; address: string | null; phone: string | null; logoUrl: string | null };
  location: { name: string; address: string | null; phone: string | null } | null;
}

function shortId(id: string) { return id.slice(-6).toUpperCase(); }
function pesos(n: number) {
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

const PAYMENT_LABELS: Record<string, string> = {
  efectivo: "Efectivo",
  transferencia: "Transferencia bancaria",
  mercadopago: "Mercado Pago",
  mp: "Mercado Pago",
};

const STATUS_LABELS: Record<string, string> = {
  PENDIENTE: "Pendiente", CONFIRMADO: "Confirmado", PAGADO: "Pagado",
  EN_PREPARACION: "En preparación", LISTO: "Listo", LISTO_RETIRO: "Listo para retiro",
  EN_CAMINO: "En camino", ENTREGADO: "Entregado", CANCELADO: "Cancelado",
  DEVUELTO: "Devuelto", REEMBOLSADO: "Reembolsado",
};

function ReceiptPrintPage() {
  const { orderId } = Route.useParams();
  const { tid } = Route.useSearch();
  const [data, setData] = useState<PrintData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!tid) { setError("Tenant no especificado."); return; }
    apiGetJson<PrintData>(`/api/orders/print?id=${orderId}`, tid)
      .then(setData)
      .catch(() => setError("No se pudo cargar la orden."));
  }, [orderId, tid]);

  useEffect(() => {
    if (!data || !tid || recorded) return;
    apiSendJson(`/api/orders/print?id=${orderId}`, "POST", { type: "receipt" }, tid)
      .catch(() => {/* no bloquear */})
      .finally(() => {
        setRecorded(true);
        setTimeout(() => window.print(), 300);
      });
  }, [data, tid, orderId, recorded]);

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }
  if (!data) {
    return <div className="p-8 text-center text-gray-500">Cargando ticket…</div>;
  }

  const { order, items, tenant, location } = data;
  const loc = location ?? { name: tenant.name, address: tenant.address, phone: tenant.phone };
  const total = order.precioFinal ?? order.precioEstimado;

  return (
    <>
      {/* ── Controles de pantalla ── */}
      <div className="no-print flex items-center justify-between gap-4 border-b bg-gray-50 px-4 py-3">
        <span className="text-sm font-medium text-gray-700">
          Ticket cliente — #{shortId(order.id)}
          {order.receiptPrintCount > 0 && (
            <span className="ml-2 text-xs text-gray-400">
              ({order.receiptPrintCount} impresión{order.receiptPrintCount !== 1 ? "es" : ""} previas)
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded bg-gray-800 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-700"
        >
          Imprimir
        </button>
      </div>

      {/* ── Vista imprimible ── */}
      <div className="thermal-page">
        {/* Encabezado */}
        <div className="thermal-header">
          <p className="thermal-business">{tenant.name}</p>
          {loc.address && <p className="thermal-meta">{loc.address}</p>}
          {loc.phone && <p className="thermal-meta">Tel: {loc.phone}</p>}
        </div>

        <div className="thermal-divider" />
        <p className="thermal-ticket-type">TICKET DE COMPRA</p>
        <div className="thermal-divider" />

        {/* Datos pedido */}
        <div className="thermal-section">
          <p><strong>Pedido #{shortId(order.id)}</strong></p>
          <p>{formatDateTime(order.createdAt)}</p>
          {order.fulfillmentMode === "scheduled" && order.scheduledFor && (
            <p><strong>Entrega programada:</strong> {formatDateTime(order.scheduledFor)}</p>
          )}
          <p>
            <strong>Entrega:</strong>{" "}
            {order.metodEntrega === "domicilio" ? "Delivery" : "Retiro en local"}
            {order.metodEntrega === "domicilio" && order.direccionEntrega && (
              <> — {order.direccionEntrega}</>
            )}
          </p>
          <p><strong>Estado:</strong> {STATUS_LABELS[order.status] ?? order.status}</p>
        </div>

        <div className="thermal-divider" />

        {/* Cliente */}
        <div className="thermal-section">
          <p><strong>{order.customerName}</strong></p>
          {order.customerPhone && <p>Tel: {order.customerPhone}</p>}
          {order.customerEmail && <p>{order.customerEmail}</p>}
        </div>

        <div className="thermal-divider" />

        {/* Detalle de ítems */}
        <div className="thermal-section">
          {items.map((item) => {
            const addonsTotal = item.selections.reduce(
              (s, a) => s + a.precioUnitario * a.cantidad, 0,
            );
            return (
              <div key={item.id} className="thermal-item">
                <div className="thermal-item-row">
                  <span>
                    {item.tipoVenta === "peso"
                      ? `${item.cantidad}${item.unidadMedida}`
                      : `${item.cantidad}x`}{" "}
                    <strong>{item.productName}</strong>
                  </span>
                  <span>{pesos(item.precioTotal)}</span>
                </div>
                {item.variantName && (
                  <p className="thermal-item-meta">  {item.variantName}</p>
                )}
                {item.selections.map((s) => (
                  <div key={s.addonProductId} className="thermal-item-row thermal-item-meta">
                    <span>  + {s.cantidad > 1 ? `${s.cantidad}x ` : ""}{s.name}</span>
                    <span>{pesos(s.precioUnitario * s.cantidad)}</span>
                  </div>
                ))}
                {addonsTotal > 0 && item.selections.length > 1 && (
                  <div className="thermal-item-row thermal-item-meta">
                    <span>  Extras:</span>
                    <span>{pesos(addonsTotal)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="thermal-divider" />

        {/* Totales */}
        <div className="thermal-section">
          {order.tienePesables && order.pesoRealKg != null && (
            <div className="thermal-item-row">
              <span>Peso real:</span>
              <span>{order.pesoRealKg} kg</span>
            </div>
          )}
          <div className="thermal-item-row thermal-total">
            <span><strong>TOTAL</strong></span>
            <span><strong>{pesos(total)}</strong></span>
          </div>
        </div>

        <div className="thermal-divider" />

        {/* Pago */}
        <div className="thermal-section">
          <p>
            <strong>Pago:</strong>{" "}
            {PAYMENT_LABELS[order.paymentMethod ?? ""] ?? order.paymentMethod ?? "—"}
            {" — "}{order.paymentStatus === "paid" ? "PAGADO ✓" : "Pendiente"}
          </p>
        </div>

        {/* Notas */}
        {order.notes && (
          <>
            <div className="thermal-divider" />
            <div className="thermal-section">
              <p className="thermal-notes-label">Notas:</p>
              <p className="thermal-notes">{order.notes}</p>
            </div>
          </>
        )}

        <div className="thermal-divider-double" />
        <p className="thermal-footer">¡Gracias por tu compra!</p>
        <p className="thermal-footer">— {tenant.name} —</p>
        <p className="thermal-footer thermal-powered">Powered by Structura Market</p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .thermal-page {
          font-family: 'Courier New', Courier, monospace;
          font-size: 12px;
          line-height: 1.4;
          max-width: 300px;
          margin: 0 auto;
          padding: 8px 4px;
          background: white;
          color: black;
        }
        .thermal-header { text-align: center; margin-bottom: 4px; }
        .thermal-business { font-size: 14px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
        .thermal-meta { font-size: 10px; color: #333; }
        .thermal-ticket-type { text-align: center; font-size: 12px; font-weight: bold; margin: 4px 0; letter-spacing: 2px; }
        .thermal-divider { border-top: 1px dashed #000; margin: 6px 0; }
        .thermal-divider-double { border-top: 2px solid #000; margin: 8px 0; }
        .thermal-section { margin: 4px 0; }
        .thermal-section p { margin: 1px 0; }
        .thermal-item { margin: 4px 0; }
        .thermal-item-row { display: flex; justify-content: space-between; gap: 4px; }
        .thermal-item-meta { font-size: 11px; color: #444; margin: 0; }
        .thermal-total { font-size: 13px; margin-top: 2px; }
        .thermal-notes-label { font-weight: bold; text-transform: uppercase; font-size: 10px; margin-bottom: 2px; }
        .thermal-notes { font-style: italic; }
        .thermal-footer { text-align: center; font-size: 10px; color: #555; margin: 2px 0; }
        .thermal-powered { font-size: 9px; color: #aaa; margin-top: 4px; }
      `}</style>
    </>
  );
}
